import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import * as MapboxDrawGeodesic from "mapbox-gl-draw-geodesic";
import drawRectangle from "mapbox-gl-draw-rectangle-mode";

const MapboxDrawContext = createContext(null);

const MapboxDrawProvider = ({ map, children }) => {
  const drawRef = useRef(null);
  const [isDrawInitialized, setDrawInitialized] = useState(false);

  useEffect(() => {
    if (!map || drawRef.current) return;

    let modes = MapboxDraw.modes;
    modes = MapboxDrawGeodesic.enable(modes);

    let customStyles = [
      // ACTIVE (being drawn)
      // line stroke
      {
        id: "gl-draw-line",
        type: "line",
        filter: ["all", ["==", "$type", "LineString"]],
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": [
            "case",
            ["==", ["get", "user_identity"], 0],
            "#ffffff", // white
            ["==", ["get", "user_identity"], 1],
            "#ff0000", // red
            ["==", ["get", "user_identity"], 2],
            "#ffff00", // yellow
            "#ffffff", // default to black
          ],
          // "line-dasharray": [0.2, 2],
          "line-width": 2,
        },
      },
      // polygon fills
      {
        id: "gl-draw-polygon-fill",
        type: "fill",
        filter: ["all", ["==", "$type", "Polygon"]],
        paint: {
          "fill-color": ["case", ["==", ["get", "user_highlight"], true], "#002bcb", "transparent"],
          "fill-opacity": ["case", ["==", ["get", "user_highlight"], true], 0.5, 0],
          // "fill-outline-color": "transparent",
          // "fill-color": "transparent",
          // "fill-opacity": 0,
          // "fill-color": [
          //   "case",
          //   ["==", ["get", "user_highlight"], true],
          //   "#002bcb", // If true, set to blue
          //   "#000000", // If false, set to transparent
          // ],
          // "fill-opacity": [
          //   "case",
          //   ["==", ["get", "user_highlight"], true],
          //   0.5, // If true, set opacity to 0.5
          //   0, // If false, set opacity to 0
          // ],
        },
      },
      {
        id: "mapbox-gl-draw-cold",
        type: "line",
        filter: ["all", ["==", "$type", "Polygon"]],
        paint: {
          "line-color": [
            "case",
            ["==", ["get", "user_identity"], 0],
            "#ffffff", // white
            ["==", ["get", "user_identity"], 1],
            "#ff0000", // red
            ["==", ["get", "user_identity"], 2],
            "#ffff00", // yellow
            "#ffffff", // default to black
          ],
          "line-width": 2,
        },
      },
      {
        id: "mapbox-gl-draw-hot",
        type: "line",
        filter: ["all", ["==", "$type", "Polygon"]],
        paint: {
          "line-color": [
            "case",
            ["==", ["get", "user_identity"], 0],
            "#ffffff", // white
            ["==", ["get", "user_identity"], 1],
            "#ff0000", // red
            ["==", ["get", "user_identity"], 2],
            "#ffff00", // yellow
            "#ffffff", // default to black
          ],
        },
      },
      // polygon mid points
      {
        id: "gl-draw-polygon-midpoint",
        type: "circle",
        filter: ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]],
        paint: {
          "circle-radius": 6,
          "circle-color": "#fbb03b",
        },
      },
      {
        id: "gl-draw-polygon-stroke-active",
        type: "line",
        filter: ["all", ["==", "$type", "Polygon"]],
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": [
            "case",
            ["==", ["get", "user_identity"], 0],
            "#ffffff", // white
            ["==", ["get", "user_identity"], 1],
            "#ff0000", // red
            ["==", ["get", "user_identity"], 2],
            "#ffff00", // yellow
            "#ffffff", // default to black
          ],
          "line-width": 2,
        },
      },
      // vertex point halos or the verticies handle circle borders
      {
        id: "gl-draw-polygon-and-line-vertex-halo-active",
        type: "circle",
        filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]],
        paint: {
          "circle-radius": 3,
          "circle-color": "#FFF",
        },
      },
      // the vertexessss
      {
        id: "gl-draw-polygon-and-line-vertex-active",
        type: "circle",
        filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]],
        paint: {
          "circle-radius": 6,
          "circle-color": "#002bcb",
        },
      },
    ];

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      userProperties: true,
      controls: {
        polygon: false,
        line_string: false,
        point: false,
        trash: false,
        combine_features: false,
        uncombine_features: false,
      },
      modes: {
        ...modes,
        draw_rectangle: drawRectangle,
      },
      styles: customStyles,
    });

    map.addControl(draw, "top-left");

    drawRef.current = draw;
    setDrawInitialized(true);

    return () => {
      if (map && draw) {
        map.removeControl(draw);
      }
    };
  }, [map]);

  return <MapboxDrawContext.Provider value={isDrawInitialized ? drawRef.current : null}>{children}</MapboxDrawContext.Provider>;
};

const useMapboxDraw = () => {
  return useContext(MapboxDrawContext);
};

export { MapboxDrawProvider, useMapboxDraw };
